import { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import IssuesActions from './IssuesActions';
import IssueComments from './IssueComments';
import IssueHeader from './IssueHeader';
import IssueInfo from './IssueInfo';
import AttachmentsGallery from './AttachmentsGallery';
import { IssueActionBanner } from './IssueActionBanner';
import Modal from './Modal';
import AppContext from '../contexts/AppContext';
import { Cross2Icon, CheckIcon, ImageIcon, CameraIcon, FileTextIcon } from '@radix-ui/react-icons';
import DocRenderer from './DocRenderer';
import UploadProgressIndicator from './UploadProgressIndicator';
import { useDeleteIssue } from '../hooks/useDeleteIssue';
import { useDeleteAttachment } from '../hooks/useDeleteAttachment';
import { hasPermission } from '../utils/permissionUtils';
import { getMappedFieldValue } from '../utils/getMappedFieldValue';

function IssueDetails() {
  const { issueKey } = useParams();
  const navigate = useNavigate();
  const { isInWebView, currentUser, serviceDesk } = useContext(AppContext);
  const [issue, setIssue] = useState(null);
  const serviceDeskConfig = issue?.serviceDesk || serviceDesk?.config;
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [forms, setForms] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [draftCanValidate, setDraftCanValidate] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const rejectCommentIsValid = useMemo(() => rejectComment.length >= 5, [rejectComment]);
  const { mutate: deleteIssue, isPending: isDeleting, isError: isDeleteError, error: deleteError } = useDeleteIssue({
    onSuccess: () => {
      navigate('/issues');
    }
  });

  const { mutate: deleteAttachment, isPending: isDeletingAttachment } = useDeleteAttachment({
    onSuccess: () => {
      setAttachmentToDelete(null);
      fetchAttachments();
    }
  });

  useEffect(() => {
    const fetchIssueDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Toujours récupérer les détails complets (assets, customFieldsLabels, etc.) depuis l'API
        const response = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}`);

        // Gestion explicite des erreurs HTTP (401/403/404 notamment)
        if (!response.ok) {
          if ([401, 403, 404].includes(response.status)) {
            setError("Ce ticket est introuvable ou vous n'y avez pas accès.");
          } else {
            setError("Impossible de charger le ticket pour le moment. Merci de réessayer plus tard.");
          }
          return;
        }

        const issue = await response.json();

        if (issue.error) {
          setError(issue.details || issue.error);
          return;
        }

        setIssue(issue);
      } catch (err) {
        console.error('Error fetching issue details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (issueKey) {
      fetchIssueDetails();
    }
  }, [issueKey]);

  const fetchAttachments = async () => {
    try {
      setAttachmentsLoading(true);
      const response = await fetchWithAuth(`issues/${encodeURIComponent(issueKey)}/attachments-comments`);
      const data = await response.json();

      if (response.ok && data.success) {
        setAttachments(data.data.attachments || []);
      }
    } catch (err) {
      console.error('Error loading attachments:', err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  useEffect(() => {
    if (issueKey) {
      fetchAttachments();
    }
  }, [issueKey]);

  const handleDeleteAttachment = (attachment) => {
    setAttachmentToDelete(attachment);
  };

  const handleConfirmDeleteAttachment = () => {
    if (!attachmentToDelete) return;
    deleteAttachment({ issueKey, attachmentId: attachmentToDelete.id });
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingCount(files.length);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('files', file);

        const response = await fetchWithAuth(
          `issues/${encodeURIComponent(issueKey)}/attachments`,
          { method: 'POST', body: formData }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          console.error(`Upload failed for ${file.name}:`, data.error);
        }
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
      } finally {
        setUploadingCount(prev => prev - 1);
      }
    }

    await fetchAttachments();

    const cameraInput = document.getElementById('issue-photo-camera');
    const galleryInput = document.getElementById('issue-photo-gallery');
    if (cameraInput) cameraInput.value = '';
    if (galleryInput) galleryInput.value = '';
  };

  const isDraft = issue && parseInt(issue.fields?.status?.id) === serviceDeskConfig?.issueValidation?.statusIdDraft;

  useEffect(() => {
    if (!isDraft || !issue || !serviceDeskConfig) return;

    const checkDraftQuota = async () => {
      if (!serviceDeskConfig?.hasQuota) return;

      const requestTypeId = Number(issue.mappedFields?.requestType?.requestType?.id);

      if (!Number.isFinite(requestTypeId)) {
        setDraftCanValidate(false);
        return;
      }

      const fields = Object.fromEntries(
        Object.entries(issue.fields || {}).filter(([k]) => k.startsWith('customfield_'))
      );

      try {
        const response = await fetchWithAuth('/quotas/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceDeskId: serviceDeskConfig.id,
            requestTypeId,
            fields,
          }),
        });

        const data = await response.json();
        setDraftCanValidate(true);
      } catch (err) {
        console.error('Error checking quota for draft:', err);
        setDraftCanValidate(false);
      }
    };

    checkDraftQuota();
  }, [isDraft, issue, serviceDeskConfig]);

  // Normaliser l'issue pour l'affichage (clé, statut, priorite, resume, dates...)
  const currentIssue = useMemo(() => {
    if (!issue) return null;
    const { key, fields, mappedFields, names } = issue;

    return {
      key,
      status: fields.status.name,
      statusCategory: fields.statusCategory,
      priority: fields.priority.name,
      summary: mappedFields.summary,
      created: fields.created,
      updated: fields.updated,
      fields,
      names,
      mappedFields,
      toBeValidated: parseInt(fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdOpen,
      toBeApproved: parseInt(fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdValidation,
    };
  }, [issue]);

  const handleFormsUpdated = (forms) => {
    setForms(forms);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    };
    return colors[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      done: 'bg-green-100 text-green-800',
      closed: 'bg-green-100 text-green-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const onSubmit = async () => {
    const transitionId = serviceDeskConfig?.issueValidation?.transitionIdOpen;

    if (!transitionId) {
      console.error('Transition ID not found');
      return;
    }

    const response = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}/transitions/${transitionId}`, {
      method: 'POST'
    });

    const data = await response.json();
    if (!response.ok || data.success === false) {
      console.error('Error transitioning issue:', data.error);
      return;
    }

    console.log('>>> Issue transitioned successfully');
    navigate('/issues');
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <svg className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/issues')}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            Retour aux tickets
          </button>
        </div>
      </div>
    );
  }

  if (!currentIssue) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Ticket non trouvé</h3>
        <button
          onClick={() => navigate('/issues')}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
        >
          Retour aux tickets
        </button>
      </div>
    );
  }

  const canSubmit = forms.length > 0 && forms.every(form => form.submitted === true);
  const techForms = forms.filter(form => form.name.toLowerCase().includes('[tech]'));
  const canTechnicianSubmit = techForms.length > 0 && techForms.every(form => form.submitted === true);

  const getTechnicianValidationMessage = () => {
    if (!canTechnicianSubmit) {
      return 'Veuillez valider votre formulaire d\'intervention.';
    }

    return 'Vous pouvez maintenant valider votre intervention !';
  };

  const getValidationMessage = () => {
    if (forms.length === 0) {
      return 'Veuillez ajouter une action à effectuer.';
    }

    if (!forms.every(form => form.submitted === true)) {
      return 'Veuillez valider toutes les actions.';
    }

    return "Validation du ticket";
  };

  const handleTransitionIssue = async (transitionId = null) => {
    if (!transitionId) {
      console.error('Transition ID not found');
      return;
    }

    const response = await fetchWithAuth(`/issues/${encodeURIComponent(issueKey)}/transitions/${transitionId}`, {
      method: 'POST'
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      console.error('Error validating issue:', data.error);
      return;
    }

    navigate('/issues');
  }

  const handleDeleteIssue = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteIssue(issueKey);
    setIsDeleteModalOpen(false);
  };

  const handleRejectIssue = () => {
    setRejectComment('');
    setIsRejectModalOpen(true);
  };

  const handleArchiveIssue = () => {
    setIsArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    try {
      await handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdArchive);
      setIsArchiveModalOpen(false);
    } catch (err) {
      console.error('Error archiving issue:', err);
      setError(err.message);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectComment.trim()) {
      return;
    }

    try {
      // Post the rejection comment
      const userDisplayName = currentUser?.name || 'Utilisateur';
      const commentTextWithAuthor = `[${userDisplayName}] ${rejectComment.trim()}`;

      const response = await fetchWithAuth(`issues/${encodeURIComponent(issueKey)}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: commentTextWithAuthor,
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to add comment');
      }

      // Then transition the issue to rejected status
      await handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdReject);

      setIsRejectModalOpen(false);
      setRejectComment('');
    } catch (err) {
      console.error('Error rejecting issue:', err);
      setError(err.message);
    }
  };

  const canValidateIssue = () => {
    if (!currentIssue) return false;
    if (currentIssue.status === 'OUVERT') return true;
    if (currentIssue.status === 'En attente de validation DRV') return hasPermission(['admin', 'manager'], currentUser.role);
    if (isDraft && draftCanValidate) return hasPermission(['admin', 'user', 'manager'], currentUser.role);
    return false;
  };

  const showValidationBanner = canValidateIssue();

  const assetsKeys = ['sector', 'shopSign', 'intervention_point', 'brand', 'furniture'];
  const assetsTranslations = {'sector': 'Secteur', 'shopSign': 'Enseigne', 'intervention_point': 'Magasin', 'brand': 'Marque', 'furniture': 'Mobilier'};
  const showActions = true;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className={`container mx-auto py-8 ${!isInWebView && showValidationBanner ? 'pb-24' : 'pb-4'}`}>
          <IssueHeader
            issue={currentIssue}
            statusColor={currentIssue.statusCategory?.colorName || getStatusColor(currentIssue.status)}
            onBack={() => navigate('/issues')}
            onDelete={Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdOpen ? handleDeleteIssue : null}
            isDeleting={isDeleting}
            isDeleteError={isDeleteError}
            deleteError={deleteError}
          />

          <div className="grid grid-cols-1 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <IssueInfo issue={currentIssue} getPriorityPill={getPriorityColor} />

              {showActions && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6">
                    <IssuesActions issue={issue} currentIssue={currentIssue} currentUser={currentUser} onFormsUpdated={handleFormsUpdated} />
                  </div>
                </div>
              )}

{assetsKeys.some(key => currentIssue.mappedFields[key]) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                        <circle cx="12" cy="12" r="6" strokeWidth={2} />
                        <circle cx="12" cy="12" r="2" strokeWidth={2} />
                      </svg>
                      Éléments de qualification
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {assetsKeys
                            .filter(key => currentIssue.mappedFields[key])
                            .map((key) => {
                              const value = currentIssue.mappedFields[key];

                              return (
                                <tr key={key} className="align-top">
                                  <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
                                    {assetsTranslations[key]}
                                  </th>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {Array.isArray(value) && value.length > 0 ? (
                                      <ul className="space-y-1">
                                        {value.map((asset, assetIndex) => (
                                          <li key={assetIndex} className="font-medium">
                                            {asset?.value || `Élément ${assetIndex + 1}`}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      getMappedFieldValue(value) || '-'
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {serviceDeskConfig?.fieldsMapping && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileTextIcon className="w-5 h-5" />
                      Détails de la demande
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="min-w-full text-sm">
                        <tbody className="divide-y divide-gray-200">
                          {(() => {
                            const allowedDetailKeys = [
                              'description',
                              'problems',
                              'assets',
                              'duedate',
                              'delivery_date',
                              'reception_date',
                              'contact_name',
                              'contact_email',
                              'contact_phone',
                              'loading_dock',
                              'delivery_time_slot',
                              'desired_intervention_date',
                              'desired_intervention_time',
                              'intervention_time',
                            ];

                            return allowedDetailKeys
                              .map((logicalKey) => {
                                // Get the field ID from the mapping
                                const fieldId = serviceDeskConfig.fieldsMapping[logicalKey] || logicalKey;

                                // Get value from issue.fields or issue.mappedFields
                                let displayValue = currentIssue?.fields?.[fieldId] || currentIssue?.mappedFields?.[logicalKey];

                                // Skip if no value exists
                                if (!displayValue)
                                  return null;

                                const isADFDoc = displayValue && typeof displayValue === 'object' && displayValue.type === 'doc';

                                // Handle different value types (except ADF doc, rendered via DocRenderer)
                                if (!isADFDoc && displayValue && typeof displayValue === 'object') {
                                  if (Array.isArray(displayValue)) {
                                    displayValue = displayValue
                                      .map(v => (v?.name || v?.label || v?.displayName || v?.value || v))
                                      .join(', ');
                                  } else {
                                    displayValue = displayValue?.name || displayValue?.label || displayValue?.displayName || displayValue?.value || displayValue?.values?.join(', ') || null;
                                  }
                                }

                                const fallbackLabel = logicalKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                const rawFieldName = serviceDeskConfig.fieldsMapping[logicalKey] || logicalKey;
                                const label = currentIssue?.names?.[rawFieldName] || fallbackLabel || logicalKey;

                                return (
                                  <tr key={logicalKey} className="align-top">
                                    <th className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left bg-gray-50">
                                      {label}
                                    </th>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {isADFDoc ? (
                                        <DocRenderer
                                          doc={currentIssue?.fields?.[fieldId] || currentIssue?.mappedFields?.[logicalKey]}
                                          className="text-sm text-gray-900"
                                        />
                                      ) : (
                                        displayValue || '-'
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                              .filter(Boolean);
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6">

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Pièces jointes
                  {attachments.length > 0 && (
                    <span className="bg-black text-white rounded-full px-2 py-0.5 text-xs font-medium">
                      {attachments.length}
                    </span>
                  )}
                </h3>
              </div>
              <div className="p-6">
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="issue-photo-camera"
                    className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                      rounded-lg text-sm font-medium text-gray-700 bg-white
                      hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                      ${uploadingCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CameraIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Prendre une photo
                  </label>
                  <input
                    type="file"
                    id="issue-photo-camera"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={uploadingCount > 0}
                    onChange={(e) => handlePhotoUpload(Array.from(e.target.files || []))}
                  />

                  <label
                    htmlFor="issue-photo-gallery"
                    className={`cursor-pointer inline-flex items-center px-5 py-3 border-2 border-gray-300
                      rounded-lg text-sm font-medium text-gray-700 bg-white
                      hover:bg-gray-50 hover:border-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
                      ${uploadingCount > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <ImageIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Ajouter depuis la galerie
                  </label>
                  <input
                    type="file"
                    id="issue-photo-gallery"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploadingCount > 0}
                    onChange={(e) => handlePhotoUpload(Array.from(e.target.files || []))}
                  />
                </div>

                <div className="mt-4">
                  <AttachmentsGallery
                    attachments={attachments}
                    loading={attachmentsLoading}
                    onImageClick={setSelectedImage}
                    editable
                    onDelete={handleDeleteAttachment}
                  />
                </div>
                <div className="mt-3">
                  <UploadProgressIndicator uploadCount={uploadingCount} />
                </div>
              </div>
            </div>

            {!serviceDeskConfig?.hideComments && (
              <IssueComments
                issueKey={issueKey}
                  currentUser={currentUser}
                  onImageClick={setSelectedImage}
                />
            )}

          </div>
        </div>
        {hasPermission(['admin', 'user', 'manager'], currentUser.role) && Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdOpen && serviceDeskConfig?.hasActions?.includes(currentIssue.mappedFields?.requestType?.requestType?.id) && (
          <IssueActionBanner
            title={getValidationMessage()}
            icon={canSubmit ? <CheckIcon className="w-5 h-5 text-green-600" /> : null}
            buttonText="Soumettre"
            buttonIcon={<CheckIcon className="w-4 h-4" />}
            onClick={onSubmit}
            disabled={!canSubmit}
            isInWebView={isInWebView}
          />
        )}
        {hasPermission(['admin', 'manager'], currentUser.role) && Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdValidation && (
          <IssueActionBanner
            title={getValidationMessage()}
            icon={<CheckIcon className="w-5 h-5 text-green-600" />}
            buttons={[
              {
                text: 'Rejeter pour modification',
                icon: <Cross2Icon className="w-4 h-4" />,
                onClick: handleRejectIssue,
                variant: 'secondary'
              },
              {
                text: 'Refuser et fermer',
                icon: <Cross2Icon className="w-4 h-4" />,
                onClick: handleArchiveIssue,
                variant: 'secondary'
              },
              {
                text: 'Valider',
                icon: <CheckIcon className="w-4 h-4" />,
                onClick: () => handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdValidation),
                variant: 'primary'
              }
            ]}
            isInWebView={isInWebView}
          />
        )}
        {isDraft && draftCanValidate && hasPermission(['admin', 'user', 'manager'], currentUser.role) && (
          <IssueActionBanner
            title="Ce brouillon peut être validé"
            icon={<CheckIcon className="w-5 h-5 text-green-600" />}
            buttonText="Valider"
            buttonIcon={<CheckIcon className="w-4 h-4" />}
            onClick={() => handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdDraftToOpen)}
            isInWebView={isInWebView}
          />
        )}
        {hasPermission(['technician'], currentUser.role) && Number(currentIssue.fields.status.id) === serviceDeskConfig?.issueValidation?.statusIdIntervention && (
          <IssueActionBanner
            title={getTechnicianValidationMessage()}
            icon={canTechnicianSubmit ? <CheckIcon className="w-5 h-5 text-green-600" /> : null}
            buttonText="Valider"
            onClick={() => handleTransitionIssue(serviceDeskConfig?.issueValidation?.transitionIdInterventionSuccess)}
            disabled={!canTechnicianSubmit}
            isInWebView={isInWebView}
          />
        )}
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors flex items-center gap-2 bg-black/50 px-4 py-2 rounded-lg text-sm"
            >
              <Cross2Icon className="w-5 h-5" />
              Fermer
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.filename}
              className="w-full max-h-96 object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Modal of deletion confirmation */}
      <Modal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onApply={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        title="Supprimer le ticket"
        description={`Êtes-vous sûr de vouloir supprimer le ticket ${currentIssue?.key} ? Cette action est irréversible.`}
        cancelLabel="Annuler"
        applyLabel={isDeleting ? "Suppression..." : "Supprimer"}
        trigger={null}
        showActions={true}
      />

      {/* Modal of rejection confirmation */}
      <Modal
        open={isRejectModalOpen}
        onOpenChange={setIsRejectModalOpen}
        onApply={handleConfirmReject}
        onCancel={() => {
          setIsRejectModalOpen(false);
          setRejectComment('');
        }}
        title="Rejeter le ticket"
        description={`Veuillez expliquer la raison du rejet du ticket ${currentIssue?.key}.`}
        cancelLabel="Annuler"
        applyLabel="Rejeter"
        trigger={null}
        showActions={true}
        disableApply={!rejectCommentIsValid}
      >
        <div className="mb-4">
          <label htmlFor="rejectComment" className="block text-sm font-medium text-gray-700 mb-2">
            Commentaire de rejet <span className="text-red-600">*</span>
          </label>
          <textarea
            id="rejectComment"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Expliquez pourquoi ce ticket est rejeté..."
            className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent resize-vertical"
            required
          />
          {!rejectCommentIsValid && (
            <p className="mt-1 text-sm text-gray-500">Ce champ est obligatoire</p>
          )}
        </div>
      </Modal>

      {/* Modal of attachment deletion confirmation */}
      <Modal
        open={!!attachmentToDelete}
        onOpenChange={(open) => { if (!open) setAttachmentToDelete(null); }}
        onApply={handleConfirmDeleteAttachment}
        onCancel={() => setAttachmentToDelete(null)}
        title="Supprimer la pièce jointe"
        description={`Êtes-vous sûr de vouloir supprimer "${attachmentToDelete?.filename}" ?`}
        cancelLabel="Annuler"
        applyLabel={isDeletingAttachment ? "Suppression..." : "Supprimer"}
        trigger={null}
        showActions={true}
        disableApply={isDeletingAttachment}
      />

      {/* Modal of archive confirmation */}
      <Modal
        open={isArchiveModalOpen}
        onOpenChange={setIsArchiveModalOpen}
        onApply={handleConfirmArchive}
        onCancel={() => setIsArchiveModalOpen(false)}
        title="Refuser et fermer le ticket"
        cancelLabel="Annuler"
        applyLabel="Refuser et fermer"
        trigger={null}
        showActions={true}
      >
        <div className="text-sm text-gray-600 space-y-3">
          <p>Vous êtes sur le point de refuser et fermer le ticket <span className="font-semibold text-gray-900">{currentIssue?.key}</span>.</p>
          <p>Le ticket restera consultable en lecture seule dans l'onglet des tickets archivés, mais ne pourra plus être modifié.</p>
          <p>Si ce ticket doit être réactivé ultérieurement, une nouvelle demande devra être créée par l'ECM.</p>
          <p className="font-medium text-gray-800">Souhaitez-vous confirmer cette action ?</p>
        </div>
      </Modal>

    </div>
  );
}

export default IssueDetails;
